import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import RoundNumber from 'common/components/ui/RoundNumber';
import { SDGTransformed } from 'sdgselection/types/SDG';
import '../styles/rating.css';

interface IProps {
  index: number;
  sdg: SDGTransformed;
  mode: 'importance' | 'control';
  rating: number;
  onRate: (sdgId: number, rate: number) => void;
}

const modeLabel: Record<'importance' | 'control', string> = {
  importance: 'Importance',
  control: 'Control',
};

const modeChipClass: Record<'importance' | 'control', string> = {
  importance: 'rating-chip--importance',
  control: 'rating-chip--control',
};

const Rating: React.FC<IProps> = ({ index, sdg, mode, rating, onRate }) => {
  const section = sdg[mode]; // importance or control

  const handleKeyPress = (rate: number, e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRate(sdg.id, rate);
    }
  };

  return (
    <article className="rating-card">
      <header className="rating-header">
        <div className="rating-header-main">
          <div className="rating-header-top-row">
            <RoundNumber 
              text={`${index + 1}`} 
              size={30} 
            />
            {rating > 0 && (
              <span className="rating-check">
                <FontAwesomeIcon icon={faCheckCircle} />
              </span>
            )}
            <span className={`rating-chip ${modeChipClass[mode]}`}>
              {modeLabel[mode]}
            </span>
          </div>

          <h4 className="rating-title">
            SDG {sdg.id} – {section.title}
          </h4>

          <p className="rating-summary">
            {section.summary}
          </p>
        </div>
      </header>

      <div className="rating-stars">
        <p className="rating-stars-label">Rate from 1 (low) to 5 (high)</p>

        <div className="rating-stars-row">
          {[1, 2, 3, 4, 5].map((rate) => (
            <span
              key={rate}
              role="button"
              tabIndex={0}
              aria-label={`Set rating to ${rate} out of 5`}
              className={`rating-star ${rating >= rate ? 'rating-star--active' : ''}`}
              onClick={() => onRate(sdg.id, rate)}
              onKeyDown={(e) => handleKeyPress(rate, e)}
            >
              <FontAwesomeIcon icon={faStar} />
            </span>
          ))}

          {rating > 0 && (
            <span className="rating-value">
              {rating}/5
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default Rating;
